import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Field,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowUploadRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '600px',
    width: '90vw',
    maxHeight: '85vh',
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
});

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (authorizationId: string) => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const classes = useStyles();
  
  const [authorizationId, setAuthorizationId] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    // Validate authorization ID
    if (authorizationId.length !== 9) {
      setValidationError('Authorization ID must be exactly 9 characters');
      return;
    }

    // Clear validation error and start loading
    setValidationError('');
    setIsLoading(true);
    
    try {
      await onSubmit(authorizationId);
      // Reset modal state on success
      setAuthorizationId('');
    } catch (error) {
      // Error handling - keep modal open
      setValidationError('Failed to submit request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while processing
    setAuthorizationId('');
    setValidationError('');
    setIsLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogTitle>
          <div className={classes.titleContainer}>
            <ArrowUploadRegular className={classes.titleIcon} />
            <span>Process Authorization</span>
          </div>
        </DialogTitle>

        <DialogBody className={classes.dialogBody}>
          {validationError && (
            <MessageBar intent="error">
              <MessageBarBody>{validationError}</MessageBarBody>
            </MessageBar>
          )}

          <Field
            label="Authorization ID"
            required
            hint="Enter a 9-character authorization ID to trigger adhoc processing"
            validationMessage={
              authorizationId.length > 0 && authorizationId.length !== 9
                ? `${authorizationId.length}/9 characters`
                : undefined
            }
            validationState={
              authorizationId.length > 0 && authorizationId.length !== 9
                ? 'warning'
                : undefined
            }
          >
            <Input
              value={authorizationId}
              onChange={(_, data) => setAuthorizationId(data.value)}
              placeholder="Enter 9-character ID"
              maxLength={9}
            />
          </Field>
        </DialogBody>

        <DialogActions>
          <Button appearance="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit}
            disabled={authorizationId.length !== 9 || isLoading}
            icon={isLoading ? <Spinner size="tiny" /> : undefined}
          >
            {isLoading ? 'Processing...' : 'Submit'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
